// ----------------------------------------------------------------
// <copyright company="Microsoft Corporation">
//     Copyright (c) Microsoft Corporation. All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
    ActionButton,
    Icon,
    SpinnerSize,
    TextField,
} from 'office-ui-fabric-react';

import BusySpinner from './../BusySpinner';

import Styles from './styles.module.scss';
import { capitalize, uniqBy } from 'lodash';
import clsx from 'clsx';

export default function RenderAccessoryBody(props: any): JSX.Element {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = React.useState(props.data ? true : false);
    const [accList, setAccList] = React.useState(Array(0));
    const [searchText, setSearchText] = React.useState<string | undefined>('');

    const [pageNumber, setPageNumber] = React.useState<number>(1);
    const [pageSet, setPageSet] = React.useState<number>(10);
    const [totalRecordCount, setTotalRecordCount] = React.useState<number>(0);
    const [totalPages, setTotalPages] = React.useState<number>(0);
    const [selectedAccessory, setSelectedAccessory] = React.useState<any[]>(Array());

    React.useEffect(() => {
        if (props.data && isLoading) {
            setIsLoading(false);
        }
    });

    React.useEffect(() => {
        setPageSet(10);
        fetchAccList(pageNumber);
    }, [props.data, searchText]);

    let renderPageNumbers;
    const columnHeaders = [
        {
            fieldName: 'name',
            name: t('name'),
            maxWidth: 835,
            minWidth: 710,
            visible: true,
        },
    ];

    function onSelectionChange(selectedItems: any) {
        return (event: React.MouseEvent) => {
            if (props.onSelect) {
                if (event.target['checked']) {
                    selectedAccessory.push(selectedItems);
                }
                else {
                    const indexOfObject = selectedAccessory.findIndex((obj: any) => {
                        return obj.sku === selectedItems.sku;
                    });
                    selectedAccessory.splice(indexOfObject, 1);
                }
                setSelectedAccessory(selectedAccessory);
                props.onSelect(selectedAccessory);
            }
        };
    }

    if (props.isLoading) {
        return <BusySpinner
            className={Styles.spinner}
            messageKey={t('loading')}
            size={SpinnerSize.large}
        />;
    }

    const pageNumbersList = [];
    if (totalRecordCount !== null) {
        const totalPagesCount = (totalRecordCount % pageSet) > 0 ? (totalRecordCount / pageSet) + 1 : totalRecordCount / pageSet;
        for (let i = 1; i <= totalPagesCount; i++) {
            pageNumbersList.push(i);
        }
        renderPageNumbers = pageNumbersList.map(number => {
            const activeClass = pageNumber === number ? Styles.active : '';
            const visibleStyle = (number >= pageNumber - 2 && number <= pageNumber + 2) ? { display: '' } : (number >= pageNumber - 2 && number <= 5 ? { display: '' } : { display: 'none' });
            const labelName = `Page ${number}`;
            return (
                <div tabIndex={0}
                    role="button"
                    aria-label={labelName}
                    key={number}
                    style={visibleStyle}
                    className={clsx(activeClass)}
                    data-arg={number}
                    onClick={handleOnClick}>{number}</div>
            );
        });
    }

    function onChangeSearchText(event: any, newValue?: string) {
        setSearchText(newValue);
    }

    function handleOnClick(event: any) {
        let targetValue: number = 0;
        const pageData = event.currentTarget.dataset.arg;
        if (pageData.toLowerCase() === 'previous') {
            targetValue = pageNumber - 1;
        }
        else if (pageData.toLowerCase() === 'next') {
            targetValue = pageNumber + 1;
        }
        else {
            targetValue = parseInt(event.currentTarget.dataset.arg!, 10);
        }
        setPageNumber(targetValue);
        fetchAccList(targetValue);
    }

    function fetchAccList(currPageNum: number) {
        setAccList(Array(0));
        while (accList.length) {
            accList.pop();
        }
        let filteredAccList: any = [];
        if (searchText && searchText !== null && searchText !== '') {
            filteredAccList = props.data.filter((x: any) => x.name.toLowerCase().indexOf(searchText.toLowerCase()) >= 0);
        }
        else {
            filteredAccList = uniqBy(props.data, (item: any) => item.name);
        }

        const startFrom = ((currPageNum - 1) * pageSet);
        const endTo = startFrom + pageSet;
        const totalCounts = filteredAccList.length;
        const totalPages = (totalCounts % pageSet) > 0 ? parseInt(((totalCounts / pageSet) + 1).toString(), 10) : totalCounts / pageSet;
        setTotalRecordCount(totalCounts);
        setTotalPages(totalPages);

        for (let index = 0; index < filteredAccList.length; index++) {
            if (index >= startFrom && index < endTo) {
                const element = filteredAccList[index];
                accList.push(element);
            }
        }
        setAccList(accList);
    }

    return (
        <div className={clsx('ms-Grid', Styles.grid)}>
            <div className="ms-Grid-row" style={{ textAlign: 'center' }}>
                <div className={clsx('ms-Grid-col', 'ms-sm12', Styles.toolbarBlock)}>
                    <div className={Styles.dvSearchBlock}>
                        <Icon iconName="Filter" />
                        <span className={Styles.dvSearchTextBlock}>
                            <TextField
                                ariaLabel={t('searchText')}
                                id="txt-search-text"
                                name="searchText"
                                onChange={onChangeSearchText}
                                placeholder={t('filter_by_keyword')}
                                value={searchText} />
                        </span>
                    </div>
                </div>
            </div>
            <div className="ms-Grid-row">
                <div className={clsx('ms-sm12', Styles.gridParent)}>
                    <table className={Styles.tableNonBordered} role="grid">
                        <thead>
                            <tr role="row">
                                {React.Children.toArray(
                                    columnHeaders.map((column: any) => (
                                        <>
                                            <th role="gridcell">
                                                <div style={{ width: column.maxWidth }}>{capitalize(column.name)}</div>
                                            </th>
                                        </>
                                    ))
                                )}
                                <th role="gridcell">
                                    <div style={{ width: 40 }}>{t('select')}</div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr role="row" style={accList && accList.length > 0 ? { display: 'none' } : { textDecorationColor: 'black' }}>
                                <td role="gridcell" className={Styles.tdNoRecordFound}>
                                    {t('no_records_found')}
                                </td>
                            </tr>
                            {React.Children.toArray(
                                accList && accList.map((element: any) => (
                                    <tr role="row">
                                        {React.Children.toArray(
                                            columnHeaders.map((column: any) => {
                                                return (<>
                                                    <td role="gridcell" style={{ width: column.maxWidth }}>
                                                        {element.name}
                                                    </td>
                                                </>);
                                            })
                                        )}
                                        <td role="gridcell" style={{ width: 40, textAlign: 'center' }}>
                                            <input type="checkbox" id={t('select')} name={t('select')}
                                                onClick={onSelectionChange(element)} />
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                <div className={Styles.pagination}>
                    <div>
                        <ActionButton
                            ariaLabel={t('FirstPage')}
                            iconProps={{
                                ariaLabel: t('FirstPage'),
                                iconName: 'Previous',
                            }}
                            data-arg={'1'}
                            onClick={handleOnClick}
                            disabled={pageNumber === 1}
                        />
                    </div>
                    <div>
                        <ActionButton
                            ariaLabel={t('PrevPage')}
                            iconProps={{
                                ariaLabel: t('PrevPage'),
                                iconName: 'CaretSolidLeft',
                            }}
                            data-arg={'Previous'}
                            onClick={handleOnClick}
                            disabled={pageNumber === 1}
                        />
                    </div>
                    {renderPageNumbers}
                    <div>
                        <ActionButton
                            ariaLabel={t('NextPage')}
                            iconProps={{
                                ariaLabel: t('NextPage'),
                                iconName: 'CaretSolidRight',
                            }}
                            data-arg={'Next'}
                            onClick={handleOnClick}
                            disabled={totalPages === 1 || pageNumber === totalPages}
                        />
                    </div>
                    <div>
                        <ActionButton
                            ariaLabel={t('LastPage')}
                            iconProps={{
                                ariaLabel: t('LastPage'),
                                iconName: 'Next',
                            }}
                            data-arg={totalPages}
                            onClick={handleOnClick}
                            disabled={totalPages === 1 || pageNumber === totalPages}
                        />
                    </div>
                </div>
                <div className={Styles.countDisplay}>
                    <div className={Styles.countDisplayText}>
                        {(((pageNumber - 1) * pageSet) + 1)}
                        &nbsp;-&nbsp;
                        {totalRecordCount < (((pageNumber - 1) * pageSet) + pageSet) ? totalRecordCount : (((pageNumber - 1) * pageSet) + pageSet)}
                        &nbsp;of&nbsp;{totalRecordCount}
                    </div>
                </div>
            </div>
        </div>
    );
}
